import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Tooltip,
  Button,
  Drawer
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  FilterList,
  Search,
  Visibility,
  MoreVert
} from '@mui/icons-material';

/**
 * Universal Responsive Data View Component
 * Mode 1: Full Data Table (Desktop >= 1024px)
 * Mode 2: Priority Table with expandable rows (Tablet 600px - 1023px)
 * Mode 3: Mobile Data Cards (< 600px)
 */
const ResponsiveDataView = ({
  columns = [],
  data = [],
  loading = false,
  loadingMessage = 'Loading data...',
  emptyMessage = 'No records found',
  rowKey = 'id',
  title = '',
  description = '',
  primaryAction = null,
  searchSlot = null,
  filterSlot = null,
  paginationSlot = null,
  onRowClick = null,
  mobileCardRender = null
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Row expansion state for Mode 2 (Tablet) and Mode 3 (Mobile)
  const [expandedRows, setExpandedRows] = useState({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getRowId = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row);
    return row[rowKey] || row.id || row.customerid || row.billid || index;
  };

  const highPriorityCols = columns.filter((c) => c.priority === 'high' || !c.priority);
  const secondaryCols = columns.filter((c) => c.priority === 'medium' || c.priority === 'low');

  const renderCellContent = (col, row) => {
    if (col.render) return col.render(row);
    const val = row[col.key];
    if (val === undefined || val === null || val === '') return '—';
    return String(val);
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Header & Controls Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff'
        }}
      >
        {/* Title and Primary Action Row */}
        {(title || primaryAction) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: searchSlot || filterSlot ? 2 : 0
            }}
          >
            {title && (
              <Box>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  {title}
                </Typography>
                {description && (
                  <Typography variant="body2" color="text.secondary">
                    {description}
                  </Typography>
                )}
              </Box>
            )}
            {primaryAction && (
              <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                {primaryAction}
              </Box>
            )}
          </Box>
        )}

        {/* Filter and Search Bar */}
        {(searchSlot || filterSlot) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 1.5
            }}
          >
            {searchSlot && (
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {searchSlot}
              </Box>
            )}

            {/* Desktop Filters vs Mobile Filter Drawer Toggle */}
            {filterSlot && (
              <>
                {isMobile ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={() => setMobileFilterOpen(true)}
                      sx={{ color: '#475569', borderColor: '#cbd5e1', borderRadius: 2 }}
                    >
                      Filter Options
                    </Button>
                    <Drawer
                      anchor="bottom"
                      open={mobileFilterOpen}
                      onClose={() => setMobileFilterOpen(false)}
                      PaperProps={{
                        sx: {
                          p: 3,
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                          maxHeight: '80vh'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700}>Filter Records</Typography>
                        <Button size="small" onClick={() => setMobileFilterOpen(false)}>Done</Button>
                      </Box>
                      <Stack spacing={2}>{filterSlot}</Stack>
                    </Drawer>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                    {filterSlot}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Main Content Area */}
      {loading ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <CircularProgress sx={{ color: '#059669', mb: 2 }} size={36} />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {loadingMessage}
          </Typography>
        </Paper>
      ) : data.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom>
            {emptyMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try adjusting your search query or clear active filters.
          </Typography>
        </Paper>
      ) : isMobile ? (
        /* MODE 3: Mobile Data Cards (<600px) */
        <Stack spacing={1.5}>
          {data.map((row, index) => {
            const id = getRowId(row, index);
            const isExpanded = Boolean(expandedRows[id]);

            if (mobileCardRender) {
              return <React.Fragment key={id}>{mobileCardRender(row, isExpanded, () => toggleRowExpand(id))}</React.Fragment>;
            }

            const primaryCol = highPriorityCols[0] || columns[0];
            const secondaryCol = highPriorityCols[1] || columns[1];
            const actionCol = columns.find((c) => c.key === 'actions' || c.label === 'Actions');

            return (
              <Card
                key={id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#ffffff',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ minWidth: 0, flexGrow: 1, pr: 1 }}>
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a" noWrap>
                        {primaryCol ? renderCellContent(primaryCol, row) : `Record #${id}`}
                      </Typography>
                      {secondaryCol && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {secondaryCol.label}: {renderCellContent(secondaryCol, row)}
                        </Typography>
                      )}
                    </Box>

                    {secondaryCols.length > 0 && (
                      <IconButton size="small" onClick={() => toggleRowExpand(id)} sx={{ color: '#64748b' }}>
                        {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>

                  {/* High Priority Attributes */}
                  <Grid container spacing={1} sx={{ mt: 0.5, mb: 1 }}>
                    {highPriorityCols.slice(2).map((col) => (
                      <Grid item xs={6} key={col.key}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {col.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#334155" noWrap>
                          {renderCellContent(col, row)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Expandable Details Drawer/Collapse */}
                  {secondaryCols.length > 0 && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Divider sx={{ my: 1.5, borderColor: '#f1f5f9' }} />
                      <Grid container spacing={1.5}>
                        {secondaryCols.map((col) => (
                          <Grid item xs={6} key={col.key}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {col.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="#334155">
                              {renderCellContent(col, row)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  )}

                  {/* Actions Row */}
                  {actionCol && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                      {renderCellContent(actionCol, row)}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        /* MODE 1 & MODE 2: Data Table (Desktop >= 1024px & Tablet 600px-1023px) */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            maxWidth: '100%',
            overflowX: 'auto'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8faf9', color: '#475569', fontWeight: 700, fontSize: '0.8125rem' } }}>
                {isTablet && secondaryCols.length > 0 && <TableCell style={{ width: 40 }} />}
                {(isTablet ? highPriorityCols : columns).map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'} style={col.width ? { width: col.width } : undefined}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                const id = getRowId(row, index);
                const isExpanded = Boolean(expandedRows[id]);
                const displayCols = isTablet ? highPriorityCols : columns;

                return (
                  <React.Fragment key={id}>
                    <TableRow
                      hover
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        '&:hover': { bgcolor: '#f8faf9' }
                      }}
                    >
                      {isTablet && secondaryCols.length > 0 && (
                        <TableCell padding="checkbox">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRowExpand(id); }}>
                            {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                          </IconButton>
                        </TableCell>
                      )}
                      {displayCols.map((col) => (
                        <TableCell key={col.key} align={col.align || 'left'}>
                          {renderCellContent(col, row)}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Mode 2 Expandable Row Content for Tablet */}
                    {isTablet && secondaryCols.length > 0 && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={displayCols.length + 1}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: '#f8faf9', borderRadius: 2, my: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                                Additional Details
                              </Typography>
                              <Grid container spacing={2}>
                                {secondaryCols.map((col) => (
                                  <Grid item xs={6} sm={4} key={col.key}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {col.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color="#334155">
                                      {renderCellContent(col, row)}
                                    </Typography>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Container */}
      {paginationSlot && (
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #e2e8f0', bgcolor: '#ffffff', mt: 2, borderRadius: 3 }}>
          {paginationSlot}
        </Box>
      )}
    </Box>
  );
};

export default ResponsiveDataView;
